import { defineMessages } from 'react-intl';

import { RPA_SKILL_WORKFLOW_METADATA_KEY } from '../../harness/tool-call-provider-metadata';

export { RPA_SKILL_WORKFLOW_METADATA_KEY };

export interface RecordedWorkflowMetadata<Presentation> {
  readonly presentation: Presentation & { readonly surfaceKind: 'ready' };
  readonly slug?: string;
  readonly inputValues: Readonly<Record<string, unknown>>;
}

export const recordedWorkflowUntitledMessage = defineMessages({
  untitled: {
    id: 'cqdmZ1cJjJ',
    defaultMessage: 'Recorded routine',
    description:
      'Fallback title when a recorded computer-use workflow has no name in provider metadata',
  },
});

export const createRecordedWorkflowMetadataReader = <
  Presentation extends { surfaceKind: string },
>(
  readPresentation: (payload: unknown, untitledFallback: string) => Presentation
) => {
  const readRpaSkillWorkflowFromMetadata = (
    metadata: Record<string, unknown> | undefined,
    inputMerge?: Record<string, unknown> | null,
    options?: { untitledFallback?: string }
  ): RecordedWorkflowMetadata<Presentation> | undefined => {
    if (!metadata) return undefined;

    const skillWorkflow = metadata[RPA_SKILL_WORKFLOW_METADATA_KEY];
    if (!isRecord(skillWorkflow)) return undefined;

    const presentation = readPresentation(
      skillWorkflow.workflow_data,
      options?.untitledFallback ?? 'Recorded routine'
    );

    if (presentation.surfaceKind !== 'ready') return undefined;

    return {
      presentation: {
        ...presentation,
        surfaceKind: 'ready',
      },
      slug: readSlug(skillWorkflow),
      inputValues: readInputValues(skillWorkflow, inputMerge),
    };
  };

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

  const readSlug = (metadata: Record<string, unknown>): string | undefined => {
    for (const key of ['slug', 'skill_slug', 'tool_slug'] as const) {
      const value = metadata[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return undefined;
  };

  const readInputValues = (
    metadata: Record<string, unknown>,
    inputMerge: Record<string, unknown> | null | undefined
  ): Readonly<Record<string, unknown>> => {
    const llmInput = inputMerge?.llm_input;
    if (isRecord(llmInput)) return llmInput;

    const snakeCase = metadata.input_values;
    if (isRecord(snakeCase)) return snakeCase;

    const camelCase = metadata.inputValues;
    return isRecord(camelCase) ? camelCase : {};
  };

  return readRpaSkillWorkflowFromMetadata;
};
